import { Program } from '../../Reports/NewReportDialog';
import { ApolloClient, FetchResult, gql } from 'apollo-boost';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { isDatePassed, isYearPassed } from 'shared/utils/date';
import { ReportVisibility } from 'shared/auth/hooks/useUserActions';
import { asContributionEligibility } from '../ContributionEligibility/ContributionEligibilitySelector';
import { removeConnections } from 'shared/apollo/client';

const newReportMutation = gql`
  mutation newReportMutation(
    $programId: Int
    $name: String
    $year: Int
    $status: String
    $stage: String
    $reportingType: String
    $visibility: String
  ) {
    newReport(
      programId: $programId
      name: $name
      year: $year
      status: $status
      stage: $stage
      reportingType: $reportingType
      visibility: $visibility
    ) {
      success
      id
    }
  }
`;

const reportsQuery = gql`
  query {
    reports {
      name
      startDate
      endDate
      year
      reportId
      programId
      status
      enabled
      version
      program {
        managerId
        name
        status
      }
    }
  }
`;

const programQuery = gql`
  {
    allPrograms(filters: { enabled: true }) {
      edges {
        node {
          Id
          name
          managerId
          enabled
          visibility
          level1Note
          level1DonorName
          isCompleted
          contributionEligibility
        }
      }
    }
  }
`;

export const getPrograms = async (client: ApolloClient<object>) => {
  const result = await client.query<{
    allPrograms: (Omit<Program, 'contributionEligibility'> & {
      contributionEligibility?: string | null;
    })[];
  }>({
    query: programQuery,
    fetchPolicy: 'no-cache',
  });
  const allPrograms = removeConnections(result.data.allPrograms) as (Omit<
    Program,
    'contributionEligibility'
  > & { contributionEligibility?: string | null })[];
  return {
    ...result,
    data: {
      allPrograms: allPrograms.map(
        (p): Program => ({
          ...p,
          contributionEligibility: asContributionEligibility(
            p.contributionEligibility,
          ),
        }),
      ),
    },
  };
};

export interface ReportsForProgram {
  year: number | string;
  name?: string;
  startDate?: string;
  endDate?: string;
  reportId: number;
  programId: number;
  status: FeedbackStatus;
  enabled?: number;
  program: {
    name: string;
    managerId: number;
    status: 'ACTIVE';
    visibility?: ReportVisibility;
  };
}

export const getReportsForProgram = (client: ApolloClient<object>) => {
  return client.query<{ reports: ReportsForProgram[] }>({
    query: reportsQuery,
    fetchPolicy: 'no-cache',
  });
};

export type SuccessResponse<K extends string> = {
  [key in K]:
    | {
        id: number;
        success: true;
      }
    | {
        id?: number;
        error: string;
        success: false;
      };
};

export type ReportingType = 'FULL' | 'LIGHT' | 'SUPER_LIGHT';

export const createNewReport = (
  client: ApolloClient<object>,
  program: Program,
  year: number | string,
  existingProgram?: Program,
  existingYear?: number,
  reportingType: ReportingType = 'FULL',
  visibility?: ReportVisibility,
) => {
  const isInThePast = isYearPassed(year);
  const stage = isInThePast ? 'Final' : 'Initial';

  return client.mutate<SuccessResponse<'newReport'>>({
    mutation: newReportMutation,
    variables: {
      year,
      name: program.name,
      programId: program.Id,
      stage,
      status: 'Draft',
      version: 'v4',
      reportingType,
      visibility,
    },
    fetchPolicy: 'no-cache',
  });
};

const updateReportMutation = gql`
  mutation updateReport(
    $reportId: Int!
    $year: Int!
    $name: String
    $programId: Int!
    $status: String
    $stage: String
    $startDate: String
    $endDate: String
    $version: String = "v4"
    $reportingType: String
  ) {
    updateReport(
      id: $reportId
      year: $year
      name: $name
      programId: $programId
      status: $status
      stage: $stage
      startDate: $startDate
      endDate: $endDate
      version: $version
      reportingType: $reportingType
    ) {
      success
    }
  }
`;

const cloneReportMutation = gql`
  mutation CloneReport($reportId: Int!, $programId: Int, $year: Int) {
    cloneReport(reportId: $reportId, programId: $programId, year: $year) {
      success
      status
      id
      errors
    }
  }
`;

export const cloneReport = (
  client: ApolloClient<object>,
  existingReportId: number,
  program: Program,
  year: number,
  startDate?: string,
  endDate?: string,
  ignoreUpdateAfter?: boolean,
  reportingType: ReportingType = 'FULL',
): Promise<FetchResult<SuccessResponse<'newReport'>>> => {
  const isPassed = endDate ? isDatePassed(endDate) : isYearPassed(year);
  const stage = isPassed ? 'Final' : 'Initial';
  return new Promise((resolve, reject) => {
    client
      .mutate<SuccessResponse<'cloneReport'>>({
        mutation: cloneReportMutation,
        variables: {
          reportId: existingReportId,
          year: year,
          programId: program.Id,
        },
        fetchPolicy: 'no-cache',
      })
      .then(result => {
        const reportId = result.data?.cloneReport.id;

        if (!result.data?.cloneReport.success || !reportId) {
          reject(result.errors);
          return;
        }

        if (ignoreUpdateAfter) {
          resolve({
            ...result,
            data: {
              newReport: {
                id: reportId,
                success: true,
              },
            },
          });
          return;
        }
        setTimeout(() => {
          client
            .mutate<SuccessResponse<'updateReport'>>({
              mutation: updateReportMutation,
              variables: {
                year,
                reportId,
                name: program.name,
                programId: program.Id,
                startDate,
                endDate,
                version: 'v4',
                status: 'Draft',
                stage,
                reportingType,
              },
              fetchPolicy: 'no-cache',
            })
            .then(r => {
              if (!r.data?.updateReport.success) {
                resolve({
                  ...result,
                  data: {
                    newReport: {
                      id: reportId,
                      success: true,
                    },
                  },
                });
                return;
              }
              resolve({
                ...result,
                data: {
                  newReport: {
                    id: reportId,
                    success: true,
                  },
                },
              });
            })
            .catch(e => {
              resolve({
                ...result,
                data: {
                  newReport: {
                    id: reportId,
                    success: true,
                  },
                },
              });
            });
        }, 1500);
      })
      .catch(e => {
        reject(e);
      });
  });
};

export const cloneReportMin = (
  client: ApolloClient<object>,
  existingReportId: number,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  return new Promise((resolve, reject) => {
    client
      .mutate<SuccessResponse<'cloneReport'>>({
        mutation: cloneReportMutation,
        variables: {
          reportId: existingReportId,
        },
        fetchPolicy: 'no-cache',
      })
      .then(result => {
        const reportId = result.data?.cloneReport.id;
        if (!result.data?.cloneReport.success || !reportId) {
          reject(result.errors);
          return;
        }
        resolve({
          ...result,
          data: {
            newReport: {
              id: reportId,
              success: true,
            },
          },
        });
      })
      .catch(e => {
        reject(e);
      });
  });
};
